import { defineComponent as _defineComponent } from 'vue'
import { offset, flip, shift } from '@floating-ui/vue'
import { computed, nextTick, ref, watch } from 'vue'

import { unwrapElement, useDismiss, usePopover } from '@/composables/popover'
import LButton from '@/components/core/Button.vue'
import {
  ensureTimeWithSeconds,
  extractTime,
  formattedDate,
} from '@/utils/date-utils'

export interface DateTimePickerProps {
  value?: boolean
  dateRange?: {
    start?: string
    end?: string
  }
  startDateLabel?: string
  endDateLabel?: string
}

export default /*#__PURE__*/_defineComponent({
  __name: 'DateTimeRangePicker',
  props: {
    value: { type: Boolean, default: undefined },
    dateRange: { default: () => ({}) },
    startDateLabel: { default: undefined },
    endDateLabel: { default: undefined }
  },
  emits: ["update:date-range", "input"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:date-range', value: { start?: string; end?: string }): void
  (e: 'input', value: boolean): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean, dateRange: {
    start?: string
    end?: string
  }, startDateLabel: string, endDateLabel: string };





// Popover Functionality -------------------------------------------------------

const internalValue = ref(false)
const isOpen = computed({
  get: () => props.value ?? internalValue.value,
  set: (value: boolean) => {
    internalValue.value = value
    emit('input', value)
  },
})

watch(isOpen, (newIsOpen) => {
  if (!newIsOpen) {
    // focus reference element when popover is closed
    nextTick(() => {
      const referenceElement = unwrapElement(referenceRef.value)
      if (!(referenceElement instanceof HTMLElement)) {
        return
      }
      // focus the trigger element directly or the reference element div
      const elementToFocus = referenceElement.firstChild ?? referenceElement
      if (elementToFocus instanceof HTMLElement) {
        elementToFocus.focus()
      }
    })
  } else {
    // focus floating element when popover is opened
    nextTick(() => {
      if (floatingRef.value instanceof HTMLElement) {
        floatingRef.value.focus()
      }
    })
  }
})

const { referenceRef, floatingRef, floatingStyles } = usePopover({
  placement: 'bottom-start',
  isOpen,
  middleware: [
    offset(4),
    flip({ padding: 16, fallbackPlacements: ['top-start'] }),
    shift({ padding: 16, crossAxis: true }),
  ],
})
useDismiss({ referenceRef, floatingRef, isOpen })

// Date ------------------------------------------------------------------------

const internalDates = ref<string[]>(
  [
    props.dateRange.start?.split('T')[0],
    props.dateRange.end?.split('T')[0],
  ].filter((value): value is string => !!value),
)
const dates = computed({
  get: () => internalDates.value,
  set: (value) => {
    if (value.length === 2 && value[0] > value[1]) {
      const tmp = value[0]
      value[0] = value[1]
      value[1] = tmp
    }
    internalDates.value = value
  },
})

// Time ------------------------------------------------------------------------

const startTime = ref(extractTime(props.dateRange.start))
const endTime = ref(extractTime(props.dateRange.end))

// Handlers --------------------------------------------------------------------

watch(
  () => props.dateRange,
  (newDateRange) => {
    dates.value = [
      newDateRange.start?.split('T')[0],
      newDateRange.end?.split('T')[0],
    ].filter((value): value is string => !!value)

    startTime.value = extractTime(newDateRange.start)
    endTime.value = extractTime(newDateRange.end)
  },
)

function saveDates() {
  if (!dates.value.length) {
    clearDates()
    return
  }

  let start = dates.value[0]
  if (startTime.value) {
    start += `T${ensureTimeWithSeconds(startTime.value)}`
  }

  let end = dates.value.length > 1 ? dates.value[1] : start
  if (endTime.value) {
    end += `T${ensureTimeWithSeconds(endTime.value)}`
  }

  emit('update:date-range', {
    start,
    end,
  })
  isOpen.value = false
}

function clearDates() {
  emit('update:date-range', {
    start: undefined,
    end: undefined,
  })
  isOpen.value = false
}

const isChromiumBrowser = computed(() => {
  return 'chrome' in window
})

return { __sfc: true,props, emit, internalValue, isOpen, referenceRef, floatingRef, floatingStyles, internalDates, dates, startTime, endTime, saveDates, clearDates, isChromiumBrowser, LButton, formattedDate }
}

})